import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { examOutcome, type ExamSessionStatus, type ExamSessionView, type MarkableAttendance } from '@nest-recruit/shared';
import { errorMessage, fieldErrorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { EXAM_SESSIONS_KEY, examSessionKey, examSessionsApi, type SessionStep } from '../api/exam-sessions-api';
import { RESULTS_KEY } from '../api/assessments-api';

const STEP_STATUS: Readonly<Record<SessionStep, ExamSessionStatus>> = {
  lock: 'roster_locked',
  start: 'in_progress',
  finish: 'results_entry',
  complete: 'completed',
};

const STEP_DONE: Readonly<Record<SessionStep, string>> = {
  lock: 'List locked',
  start: 'Exam started — take attendance',
  finish: 'Exam finished — enter the marks',
  complete: 'Session completed',
};

/**
 * ASM-04/05's actions. Each change shows at once and the server's answer replaces it;
 * a refusal puts the screen back and says why (CLAUDE.md §5a rules 1 and 3).
 */
export function useSessionActions(sessionId: string) {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const key = examSessionKey(sessionId);

  const run = useCallback(
    async (failure: string, call: () => Promise<ExamSessionView>, optimistic?: (view: ExamSessionView) => ExamSessionView): Promise<ExamSessionView | null> => {
      const snapshot = queryClient.getQueryData<ExamSessionView>(key);
      if (snapshot && optimistic) queryClient.setQueryData(key, optimistic(snapshot));
      try {
        const view = await call();
        queryClient.setQueryData(key, view);
        void queryClient.invalidateQueries({ queryKey: [...EXAM_SESSIONS_KEY, 'list'] });
        void queryClient.invalidateQueries({ queryKey: RESULTS_KEY });
        return view;
      } catch (error) {
        queryClient.setQueryData(key, snapshot);
        show({ tone: 'danger', message: failure, detail: fieldErrorMessage(error) });
        return null;
      }
    },
    [queryClient, key, show],
  );

  const step = useCallback(
    async (view: ExamSessionView, which: SessionStep): Promise<void> => {
      const done = await run(`That didn't work`, () => examSessionsApi.step(sessionId, which, view.version), (current) => ({ ...current, status: STEP_STATUS[which] }));
      if (done) show({ tone: 'success', message: STEP_DONE[which] });
    },
    [run, sessionId, show],
  );

  const attendance = useCallback(
    async (marks: readonly { readonly attendanceId: string; readonly status: MarkableAttendance }[]): Promise<void> => {
      const byId = new Map(marks.map((mark) => [mark.attendanceId, mark.status]));
      await run("Attendance wasn't saved", () => examSessionsApi.attendance(sessionId, marks), (current) => ({
        ...current,
        roster: current.roster.map((row) => ({ ...row, status: byId.get(row.attendanceId) ?? row.status })),
      }));
    },
    [run, sessionId],
  );

  const result = useCallback(
    async (attendanceId: string, score: number): Promise<void> => {
      await run("The mark wasn't saved", () => examSessionsApi.results(sessionId, [{ attendanceId, score }]), (current) => ({
        ...current,
        roster: current.roster.map((row) =>
          row.attendanceId === attendanceId ? { ...row, status: 'completed' as const, score, didPass: examOutcome(score, current.maxScore, current.passPercent).didPass } : row,
        ),
      }));
    },
    [run, sessionId],
  );

  const remove = useCallback(
    async (attendanceId: string, candidateName: string): Promise<void> => {
      const done = await run(`${candidateName} wasn't taken off`, () => examSessionsApi.remove(sessionId, attendanceId), (current) => ({
        ...current,
        booked: current.booked - 1,
        roster: current.roster.filter((row) => row.attendanceId !== attendanceId),
      }));
      if (done) show({ tone: 'success', message: `${candidateName} taken off the list`, detail: 'They were told by email, and can be booked on another session.' });
    },
    [run, sessionId, show],
  );

  const reschedule = useCallback(
    async (view: ExamSessionView, startsAt: string, venue: string): Promise<boolean> => {
      const done = await run("The session wasn't moved", () => examSessionsApi.reschedule(sessionId, view.version, startsAt, venue), (current) => ({ ...current, startsAt, venue, status: 'scheduled' }));
      if (done) show({ tone: 'success', message: 'Session moved', detail: `Everyone booked (${done.booked}) was told by email.` });
      return done !== null;
    },
    [run, sessionId, show],
  );

  const cancel = useCallback(
    async (view: ExamSessionView, reason: string): Promise<void> => {
      const done = await run("The session wasn't cancelled", () => examSessionsApi.cancel(sessionId, view.version, reason), (current) => ({ ...current, status: 'cancelled', statusReason: reason }));
      if (done) show({ tone: 'success', message: 'Session cancelled', detail: `Everyone booked (${done.booked}) was told by email.` });
    },
    [run, sessionId, show],
  );

  const add = useCallback(
    async (applicationIds: readonly string[]): Promise<void> => {
      try {
        const outcome = await examSessionsApi.add(sessionId, applicationIds);
        await Promise.all([queryClient.invalidateQueries({ queryKey: key }), queryClient.invalidateQueries({ queryKey: [...EXAM_SESSIONS_KEY, 'candidates', sessionId] })]);
        const [first] = outcome.refused;
        if (first) {
          show({
            tone: outcome.added.length > 0 ? 'warning' : 'danger',
            message: `${outcome.added.length} added, ${outcome.refused.length} not added`,
            detail: outcome.refused
              .slice(0, 2)
              .map((row) => `${row.candidateName}: ${row.reason}`)
              .join(' '),
          });
          return;
        }
        show({ tone: 'success', message: `${outcome.added.length} added to the list`, detail: 'Each has been sent a booking email.' });
      } catch (error) {
        show({ tone: 'danger', message: 'Nobody was added', detail: errorMessage(error) });
      }
    },
    [queryClient, key, sessionId, show],
  );

  /** "Copy message" (S18 Q4): the booking or cancellation wording, for WhatsApp. Logged. */
  const copy = useCallback(
    async (attendanceId: string, candidateName: string): Promise<void> => {
      try {
        const { text } = await examSessionsApi.message(sessionId, attendanceId);
        await navigator.clipboard.writeText(text);
        show({ tone: 'success', message: `Message copied for ${candidateName}`, detail: 'Paste it into WhatsApp or SMS.' });
      } catch (error) {
        show({ tone: 'danger', message: "The message wasn't copied", detail: errorMessage(error) });
      }
    },
    [sessionId, show],
  );

  return { step, attendance, result, remove, reschedule, cancel, add, copy };
}
